// Besoins et consultations (M15, lot L4-C, D-147, D-180) : besoins validés de la chaîne de la dépense à
// consulter, consultations avec leur procédure, offres reçues, analyse (conformité, note technique),
// attribution motivée par le maire ou président et contrat. Les actes n'apparaissent qu'avec le droit
// correspondant ; le serveur refait tous les contrôles.
import type { BesoinDepense, Consultation, OffreAchat } from '@cc/contrats';
import { classerOffres } from '@cc/contrats';
import { Bouton, EtatVide, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { INVALIDER_ACHATS, transiter, useBesoinsValides, useConsultations } from './api';
import { FormulaireContrat } from './FormulairesContrats';
import { FormulaireAnalyse, FormulaireAttribution, FormulaireConsultation, FormulaireOffre } from './FormulairesConsultation';
import { actesConsultation, francs, libelleConsultation, libelleOffre, TON_CONSULTATION, TON_OFFRE } from './logique';
import { NavigationAchats } from './NavigationAchats';
import styles from './achats.module.css';

type Dialogue =
  | { type: 'consultation'; besoin: BesoinDepense | null }
  | { type: 'offre' | 'attribution' | 'contrat'; consultation: Consultation }
  | { type: 'analyse'; offre: OffreAchat; fictif: boolean }
  | null;

function Offres({ c, onAnalyser, analyser }: { c: Consultation; onAnalyser: (o: OffreAchat) => void; analyser: boolean }) {
  const rang = new Map(classerOffres(c.offres).map((o, i) => [o.id, i + 1]));
  const colonnes: ColonneTableau<OffreAchat>[] = [
    { id: 'fournisseur', entete: 'Fournisseur', enTeteLigne: true, masquable: false, cellule: (o) => o.fournisseur.nom },
    { id: 'montant', entete: 'Montant', numerique: true, cellule: (o) => francs(o.montant, c.fictif), valeurTri: (o) => o.montant },
    { id: 'delai', entete: 'Délai (jours)', numerique: true, cellule: (o) => o.delai_jours },
    { id: 'note', entete: 'Note technique', numerique: true, cellule: (o) => (o.note_technique === null ? 'non notée' : o.note_technique) },
    { id: 'rang', entete: 'Classement', numerique: true, cellule: (o) => rang.get(o.id) ?? '' },
    { id: 'etat', entete: 'État', cellule: (o) => <PuceEtat ton={TON_OFFRE[o.etat]} libelle={libelleOffre(o.etat)} /> },
    { id: 'actes', entete: 'Actes', masquable: false, cellule: (o) => (analyser ? <Bouton variante="secondaire" onClick={() => onAnalyser(o)}>Analyser</Bouton> : null) },
  ];
  if (c.offres.length === 0) return <p className={styles.detail}>Aucune offre reçue.</p>;
  return <Tableau legende={`Offres de ${c.numero}`} colonnes={colonnes} lignes={c.offres} cleLigne={(o) => o.id} cleColonnes="achats-offres" />;
}

export function PageConsultations() {
  const { contexte } = useSession();
  const consultations = useConsultations();
  const besoins = useBesoinsValides();
  const [d, setD] = useState<Dialogue>(null);
  const [ouverte, setOuverte] = useState<string | null>(null);
  const droits = { modifier: peut(contexte, 'achat', 'modifier'), valider: peut(contexte, 'achat', 'valider'), decider: peut(contexte, 'achat', 'decider'), signer: peut(contexte, 'achat', 'signer') };
  const transition = useAction({ executer: (e: { id: string; vers: 'publiee' | 'analyse' | 'infructueuse' | 'annulee'; motif?: string }) => transiter(e.id, { vers: e.vers, motif: e.motif }), succes: (c) => `Consultation ${c.numero} : ${libelleConsultation(c.etat).toLowerCase()}.`, invalider: INVALIDER_ACHATS });
  const liste = consultations.data?.elements ?? [];
  const avecConsultation = new Set(liste.map((c) => c.besoin?.id).filter(Boolean));
  const aConsulter = (besoins.data?.elements ?? []).filter((b) => !avecConsultation.has(b.id));
  const colonnes: ColonneTableau<Consultation>[] = [
    { id: 'numero', entete: 'Consultation', enTeteLigne: true, masquable: false, cellule: (c) => <span className="cc-mono">{c.numero}</span>, valeurTri: (c) => c.numero },
    { id: 'objet', entete: 'Objet', cellule: (c) => c.objet },
    { id: 'procedure', entete: 'Procédure', cellule: (c) => c.procedure.libelle },
    { id: 'estimation', entete: 'Estimation', numerique: true, cellule: (c) => francs(c.estimation, c.fictif), valeurTri: (c) => c.estimation },
    { id: 'limite', entete: 'Date limite', cellule: (c) => c.date_limite },
    { id: 'offres', entete: 'Offres', numerique: true, cellule: (c) => c.offres.length },
    { id: 'etat', entete: 'État', cellule: (c) => <PuceEtat ton={TON_CONSULTATION[c.etat]} libelle={libelleConsultation(c.etat)} /> },
    {
      id: 'actes', entete: 'Actes', masquable: false, cellule: (c) => {
        const a = actesConsultation(c, droits);
        return (
          <div className={styles.actesLigne}>
            <Bouton variante="discret" onClick={() => setOuverte(ouverte === c.id ? null : c.id)} aria-expanded={ouverte === c.id}>{ouverte === c.id ? 'Masquer les offres' : 'Voir les offres'}</Bouton>
            {a.includes('publier') ? <Bouton variante="principal" chargement={transition.enCours} onClick={() => transition.lancer({ id: c.id, vers: 'publiee' })}>Publier</Bouton> : null}
            {a.includes('offre') ? <Bouton variante="secondaire" onClick={() => setD({ type: 'offre', consultation: c })}>Offre reçue</Bouton> : null}
            {a.includes('analyse') ? <Bouton variante="secondaire" chargement={transition.enCours} onClick={() => transition.lancer({ id: c.id, vers: 'analyse' })}>Clore la réception</Bouton> : null}
            {a.includes('attribuer') ? <Bouton variante="principal" onClick={() => setD({ type: 'attribution', consultation: c })}>Attribuer</Bouton> : null}
            {a.includes('contrat') ? <Bouton variante="principal" onClick={() => setD({ type: 'contrat', consultation: c })}>Établir le contrat</Bouton> : null}
          </div>
        );
      },
    },
  ];
  const detail = liste.find((c) => c.id === ouverte) ?? null;
  return (
    <>
      <EnTetePage
        etiquette="Marchés publics et fournisseurs"
        titre="Besoins et consultations"
        description="Consultations des fournisseurs selon les procédures paramétrées par la collectivité : aucun seuil de la commande publique n'est prérempli."
        actions={droits.modifier ? <Bouton variante="principal" icone={Plus} onClick={() => setD({ type: 'consultation', besoin: null })}>Nouvelle consultation</Bouton> : null}
      />
      <NavigationAchats actif="consultations" />
      <section className={styles.bloc} aria-labelledby="titre-besoins-achat">
        <h2 id="titre-besoins-achat">Besoins validés à consulter</h2>
        {besoins.error ? <p className={styles.detail}>Les besoins de la chaîne de la dépense ne sont pas lisibles avec vos droits.</p> : aConsulter.length === 0 ? <p className={styles.detail}>Aucun besoin validé en attente de consultation.</p> : (
          <ul className={styles.liste}>
            {aConsulter.map((b) => (
              <li key={b.id}>
                {b.objet} ({b.service.nom ?? 'service'}, estimation {francs(b.estimation)}){' '}
                {droits.modifier ? <Bouton variante="discret" onClick={() => setD({ type: 'consultation', besoin: b })}>Lancer une consultation</Bouton> : null}
              </li>
            ))}
          </ul>
        )}
      </section>
      <EtatRequete chargement={consultations.isPending} erreur={consultations.error} onReessayer={() => void consultations.refetch()} lot="L4-C" fonction="Les consultations">
        <section className={styles.bloc} aria-labelledby="titre-consultations">
          <h2 id="titre-consultations">Consultations</h2>
          {transition.messageErreur ? <p role="alert" className={styles.negatif}>{transition.messageErreur}</p> : null}
          {liste.length === 0 ? <EtatVide titre="Aucune consultation"><p>Aucune consultation n&apos;est ouverte.</p></EtatVide> : <Tableau legende="Consultations" colonnes={colonnes} lignes={liste} cleLigne={(c) => c.id} cleColonnes="achats-consultations" />}
        </section>
        {detail ? (
          <section className={styles.bloc} aria-labelledby="titre-offres">
            <h2 id="titre-offres">Offres de la consultation {detail.numero}</h2>
            {detail.attribution ? <p className={styles.detail}>Attribuée à {detail.attribution.fournisseur_nom} pour {francs(detail.attribution.montant, detail.fictif)} : {detail.attribution.motif}</p> : null}
            <Offres c={detail} analyser={detail.etat === 'analyse' && droits.valider} onAnalyser={(o) => setD({ type: 'analyse', offre: o, fictif: detail.fictif })} />
          </section>
        ) : null}
      </EtatRequete>
      {d?.type === 'consultation' ? <FormulaireConsultation besoin={d.besoin} onFermer={() => setD(null)} /> : null}
      {d?.type === 'offre' ? <FormulaireOffre consultation={d.consultation} onFermer={() => setD(null)} /> : null}
      {d?.type === 'analyse' ? <FormulaireAnalyse offre={d.offre} fictif={d.fictif} onFermer={() => setD(null)} /> : null}
      {d?.type === 'attribution' ? <FormulaireAttribution consultation={d.consultation} onFermer={() => setD(null)} /> : null}
      {d?.type === 'contrat' ? <FormulaireContrat consultation={d.consultation} onFermer={() => setD(null)} /> : null}
    </>
  );
}
